import {
  ColumnDataType,
  TableColumnFormatType,
  type TableColumnHashingAlgorithm,
} from "@decentriq/graphql/dist/types";
import { type DataRoomTableColumn } from "models";
import { computeCacheKeyString } from "utils";

export interface BaseAudienceDatasetsHashes {
  matchingDatasetHash: string | null;
  segmentsDatasetHash: string | null;
  demographicsDatasetHash: string | null;
  embeddingsDatasetHash: string | null;
}

/**
 * This class is used to store the hashes of the datasets that are used in the media data room
 * It also contains audiences node dataset hash and the combined hash of all datasets (advertiser & publisher) which are used as a part of the key to store the cache.
 * Also this class provides a methods to update certain hashes which are used then during provisioning/deprovisioning of the data room data.
 */
export class PublishedDatasetsHashes {
  /**
   * Creates a cache key object from the provided advertiser & publisher datasets hashes
   */
  static datasetsCacheKeyCreator = (
    advertiserDatasetHash: string | null,
    publisherDatasetsHashes: BaseAudienceDatasetsHashes
  ) => {
    if (
      !advertiserDatasetHash ||
      !publisherDatasetsHashes?.matchingDatasetHash
    ) {
      return null;
    }
    return {
      advertiserDatasetHash,
      matchingDatasetHash: publisherDatasetsHashes.matchingDatasetHash,
      ...(publisherDatasetsHashes.segmentsDatasetHash
        ? {
            publisherSegmentsDatasetHash:
              publisherDatasetsHashes.segmentsDatasetHash,
          }
        : {}),
      ...(publisherDatasetsHashes.demographicsDatasetHash
        ? {
            publisherDemographicsDatasetHash:
              publisherDatasetsHashes.demographicsDatasetHash,
          }
        : {}),
      ...(publisherDatasetsHashes.embeddingsDatasetHash
        ? {
            publisherEmbeddingsDatasetHash:
              publisherDatasetsHashes.embeddingsDatasetHash,
          }
        : {}),
    };
  };

  /**
   * Creates a cache key string from the provided advertiser & publisher datasets hashes
   */
  static datasetsCacheKeyAsStringCreator = (
    advertiserDatasetHash: string | null,
    publisherDatasetsHashes: BaseAudienceDatasetsHashes
  ) => {
    const cacheObj = PublishedDatasetsHashes.datasetsCacheKeyCreator(
      advertiserDatasetHash,
      publisherDatasetsHashes
    );
    if (!cacheObj) {
      return null;
    }
    return computeCacheKeyString(cacheObj);
  };

  static defaultPublisherDatasetsHashes = {
    demographicsDatasetHash: null,
    embeddingsDatasetHash: null,
    matchingDatasetHash: null,
    segmentsDatasetHash: null,
  };

  constructor(
    public readonly advertiserDatasetHash: string | null = null,
    public readonly publisherDatasetsHashes: BaseAudienceDatasetsHashes = PublishedDatasetsHashes.defaultPublisherDatasetsHashes,
    public readonly audiencesDatasetHash: string | null = null,
    public readonly datasetsHash: string | null | undefined = undefined
  ) {
    this.datasetsHash =
      datasetsHash === undefined
        ? PublishedDatasetsHashes.datasetsCacheKeyAsStringCreator(
            advertiserDatasetHash,
            publisherDatasetsHashes
          )
        : datasetsHash;
  }

  /**
   * Returns true if advertiser datasets are present
   */
  public get hasAdvertiserData() {
    return this.advertiserDatasetHash !== null;
  }

  /**
   * Returns true if publisher datasets are present
   */
  public get hasPublisherData() {
    return this.publisherDatasetsHashes.matchingDatasetHash !== null;
  }

  /**
   * Returns true if both advertiser and publisher datasets are present
   */
  public get hasRequiredData() {
    return this.hasAdvertiserData && this.hasPublisherData;
  }

  /**
   * This method is used to update the `audiencesDatasetHash` either when the audiences updated or when audiences data node is deprovisioned
   * @param audiencesDatasetHash hash of the audiences node dataset in the data room
   * @returns new instanse of `PublishedDatasetsHashes` with updated `audiencesDatasetHash`
   */
  updateAudiencesDatasetHash(audiencesDatasetHash: string | null) {
    return new PublishedDatasetsHashes(
      this.advertiserDatasetHash,
      this.publisherDatasetsHashes,
      audiencesDatasetHash,
      this.datasetsHash
    );
  }

  /**
   * This method is used to update the `advertiserDatasetHash`. It will recalculate `datasetsHash` using new value of `advertiserDatasetHash`
   * @param advertiserDatasetHash hash of the advertiser data provisioned in the data room
   * @returns new instanse of `PublishedDatasetsHashes` with updated `advertiserDatasetHash`
   */
  updateAdvertiserDatasetHash(advertiserDatasetHash: string | null) {
    return new PublishedDatasetsHashes(
      advertiserDatasetHash,
      this.publisherDatasetsHashes,
      this.audiencesDatasetHash
    );
  }

  /**
   * This method is used to update the `publisherDatasetsHashes`. It will recalculate `datasetsHash` using new value of `publisherDatasetsHashes`
   * @param publisherDatasetsHashes hashes of the publisher data provisioned in the data room
   * @returns new instanse of `PublishedDatasetsHashes` with updated `publisherDatasetsHashes`
   */
  updatePublisherDatasetsHashes(
    publisherDatasetsHashes: BaseAudienceDatasetsHashes
  ) {
    return new PublishedDatasetsHashes(
      this.advertiserDatasetHash,
      publisherDatasetsHashes,
      this.audiencesDatasetHash
    );
  }

  /**
   * This method can be used to create an instance of `PublishedDatasetsHashes` with the provided datasets hashes object which
   * might not include all the datasets hashes, thats usefull to only include what is important for execution context
   * @param datasetsHashObj object with the datasets hashes
   * @returns instance of the `PublishedDatasetsHashes`
   */
  updateDatasetsHash(datasetsHashObj: Record<string, string> | null) {
    return new PublishedDatasetsHashes(
      this.advertiserDatasetHash,
      this.publisherDatasetsHashes,
      this.audiencesDatasetHash,
      datasetsHashObj ? computeCacheKeyString(datasetsHashObj) : null
    );
  }
}

export interface LegacyValidationReport {
  report: {
    num_deduplicated_rows: number;
    num_ingested_rows: number;
  };
  version: "legacy";
}

export const getAdvertiserAudienceColumns: (
  matchingIdFormat: TableColumnFormatType,
  hashMatchingIdWith: TableColumnHashingAlgorithm | null
) => DataRoomTableColumn[] = (matchingIdFormat, hashMatchingIdWith) => [
  {
    formatType: matchingIdFormat,
    hashWith: hashMatchingIdWith ?? undefined,
    id: "matchingId",
    name: "matchingId",
    nullable: false,
    primitiveType: ColumnDataType.Text,
    tableNodeId: "audience_leaf",
  },
  {
    default: "All customers",
    formatType: TableColumnFormatType.String,
    id: "audienceType",
    name: "audienceType",
    nullable: false,
    primitiveType: ColumnDataType.Text,
    tableNodeId: "audience_leaf",
  },
];
